function text(value) {
  return String(value ?? '').trim();
}

function normalizedName(value) {
  return text(value).replace(/\s+/g, ' ').toLocaleLowerCase('ko-KR');
}

function memberOpenid(value = {}) {
  return text(value.memberOpenid ?? value.member_openid ?? value.openid);
}

function memberNickname(value = {}) {
  return text(value.nickname ?? value.memberNickname ?? value.member_nickname) || '닉네임 확인 불가';
}

function recordReferences(records = []) {
  const openids = new Set();
  const names = new Set();

  for (const record of Array.isArray(records) ? records : []) {
    const openid = memberOpenid(record);
    const nickname = normalizedName(memberNickname(record));
    if (openid) openids.add(openid);
    if (nickname && nickname !== normalizedName('닉네임 확인 불가')) names.add(nickname);
  }

  return { openids, names };
}

function normalizeMember(member = {}, source = 'fallback') {
  const openid = memberOpenid(member);
  const nickname = memberNickname(member);
  if (!openid && nickname === '닉네임 확인 불가') return null;

  return {
    ...member,
    openid,
    memberOpenid: openid,
    nickname,
    source,
  };
}

export function composeUnionMembers({
  rosterMembers = [],
  fallbackMembers = [],
  records = [],
  maxMembers = 64,
} = {}) {
  const referenced = recordReferences(records);
  const byOpenid = new Map();
  const byName = new Map();
  const output = [];

  const add = (rawMember, source) => {
    const member = normalizeMember(rawMember, source);
    if (!member) return;

    const nameKey = normalizedName(member.nickname);
    const existing = (member.memberOpenid && byOpenid.get(member.memberOpenid))
      || (nameKey && byName.get(nameKey));

    if (existing) {
      if (!existing.memberOpenid && member.memberOpenid) {
        existing.memberOpenid = member.memberOpenid;
        existing.openid = member.memberOpenid;
        byOpenid.set(member.memberOpenid, existing);
      }
      if (existing.nickname === '닉네임 확인 불가' && member.nickname !== existing.nickname) {
        existing.nickname = member.nickname;
      }
      if (source === 'roster') existing.source = 'roster';
      return;
    }

    output.push(member);
    if (member.memberOpenid) byOpenid.set(member.memberOpenid, member);
    if (nameKey) byName.set(nameKey, member);
  };

  const normalizedRoster = Array.isArray(rosterMembers) ? rosterMembers : [];
  for (const member of normalizedRoster) add(member, 'roster');

  const hasRoster = normalizedRoster.length > 0;
  for (const member of Array.isArray(fallbackMembers) ? fallbackMembers : []) {
    const openid = memberOpenid(member);
    const nameKey = normalizedName(memberNickname(member));
    const hasRecord = (openid && referenced.openids.has(openid))
      || (nameKey && referenced.names.has(nameKey));
    if (!hasRoster || hasRecord) add(member, 'fallback');
  }

  for (const record of Array.isArray(records) ? records : []) {
    add({
      memberOpenid: memberOpenid(record),
      nickname: memberNickname(record),
    }, 'record');
  }

  return output
    .sort((left, right) => left.nickname.localeCompare(right.nickname, 'ko-KR'))
    .slice(0, Math.max(1, Number(maxMembers) || 64));
}
